import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import arrowDownIcon from "../../assets/images/arrow-down.svg";
import closeIcon from "../../assets/images/close.svg";
import header90YearsLogoWhite from "../../assets/images/header-90-years-logo-white.png";
import header90YearsLogo from "../../assets/images/header-90-years-logo.png";
import logoSmallIcon from "../../assets/images/logo-symbol_2.svg";
import logoIcon from "../../assets/images/logotype_2.svg";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import useTranslations from "../../hooks/useTranslations";
import { SiteSettings } from "../../models/shared/SiteSettings";
import SanityLink from "../SanityLink";
import { GroupNavigation } from "./GroupNavigation";
import { HeaderTheme } from "./HeaderTheme";
import { MobileNavigationButton } from "./MobileNavigationButton";
import { createNavigation } from "./NavigationFactory";
import { NavigationSection } from "./NavigationSection";
import { GoToMainContentBtn } from "./Tools/GoToMainContentBtn";
import { ToolItem } from "./Tools/ToolItem";

interface Props {
  siteSettings: SiteSettings;
  transparent?: boolean;
}

const smallToLargeBreakpoint = 1280;

export const GroupHeader = ({ siteSettings, transparent }: Props) => {
  const translations = useTranslations();

  const headerRef = useRef<HTMLElement>(null);

  const mainNavigation = useMemo(
    () => (siteSettings ? createNavigation(siteSettings) : []),
    [siteSettings]
  );

  const [navigationState, selectItem] = useState<{
    selectedItemIndex: number | null;
    selectedItem: NavigationSection | null;
  }>();

  const [mobileNavigationOpen, toggleMobileMenu] = useState(false);

  const [isMobile, checkIfMobile] = useState(false);

  const [isIntersecting, setIntersecting] = useState(true);

  useEffect(() => {
    if (typeof document === "undefined" || !headerRef?.current) {
      return;
    }

    const stickyMenuObserver = new IntersectionObserver(
      ([entry]) => setIntersecting(entry.isIntersecting),
      {
        root: null,
        rootMargin: "40px",
        threshold: 1.0
      }
    );

    stickyMenuObserver.observe(headerRef.current);

    return () => {
      stickyMenuObserver.disconnect();
    };
  }, []);

  useBreakpoint(smallToLargeBreakpoint, eqAbove => checkIfMobile(!eqAbove));

  // Close on esc or click outside
  const closeCallback = useCallback(() => selectItem(undefined), []);

  const onEscPressed = useCallback(
    (e: KeyboardEvent) => {
      if (!navigationState || navigationState.selectedItemIndex === null) {
        return;
      }

      if (e.key === "Escape") {
        closeCallback();
      }
    },
    [closeCallback, navigationState]
  );

  const onClickOutside = () => closeCallback();

  const onClickInside = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
  };

  useEffect(() => {
    if (typeof window === "undefined" || navigationState === null) {
      return;
    }

    window.addEventListener("keydown", onEscPressed, false);

    return () => {
      window.removeEventListener("keydown", onEscPressed, false);
    };
  }, [navigationState, onEscPressed]);

  const onItemSelected = (index: number) => {
    if (index === undefined || index === -1) {
      selectItem(undefined);
      return;
    }

    if (navigationState && index === navigationState.selectedItemIndex) {
      selectItem(undefined);
    } else {
      selectItem({
        selectedItemIndex: index,
        selectedItem: mainNavigation[index]
      });
    }
  };

  return (
    <header
      ref={headerRef}
      className={`header-fixed-container h-12 ${
        transparent ? "lg:h-0" : "lg:h-30 xl:h-34"
      }`}
    >
      <div
        className={`header ${
          transparent && isIntersecting ? "header--transparent" : "header--solid"
        } h-12 lg:h-12 xl:h-34`}
        onClick={onClickOutside}
      >
        <GoToMainContentBtn title={translations.goToMainContent} />
        <div className="header-bar">
          <div className="header-main">
            <SanityLink
              className="header-logo-mobile focus:shadow-outline"
              aria-label={translations.home}
            >
              <svg width={30} height={24}>
                <use xlinkHref={logoSmallIcon.url} />
              </svg>
            </SanityLink>
            <SanityLink
              className="header-logo-desktop focus:shadow-outline"
              aria-label={translations.home}
            >
              <svg width={165} height={25}>
                <use xlinkHref={logoIcon.url} />
              </svg>
            </SanityLink>
            <div className="self-center w-full flex justify-center">
              <a href="https://www.anticimex.com/anticimex-90th-anniversary/">
                <img
                  src={
                    transparent && isIntersecting && !isMobile
                      ? header90YearsLogoWhite
                      : header90YearsLogo
                  }
                  alt={
                    translations.anniversaryImageAlt || "Anticimex 90 years of innovation"
                  }
                  width={120}
                  height={32}
                />
              </a>
            </div>
            {isMobile && (
              <div
                className="header-navigation justify-end xl:hidden"
                onClick={onClickInside}
              >
                <MobileNavigationButton
                  active={mobileNavigationOpen}
                  onClick={() => toggleMobileMenu(!mobileNavigationOpen)}
                />
              </div>
            )}
          </div>

          {!!siteSettings?.toolNavigation && (
            <div className="header-tools hidden xl:flex">
              {siteSettings.toolNavigation.map((item, index) => (
                <ToolItem
                  key={index}
                  {...item}
                  targetDevice="desktop"
                  showSeparator={index !== 0}
                  transparentBackground={transparent && isIntersecting}
                />
              ))}
            </div>
          )}
        </div>
        {!isMobile && !!mainNavigation && (
          <div
            key="group-navigation-desktop"
            className="header-navigation-lower"
            onClick={onClickInside}
          >
            <nav key="header-navigation" className="header-navigation lg:h-14">
              {mainNavigation.map((item, index) => {
                const isOpen = navigationState?.selectedItemIndex === index;

                return (
                  <button
                    key={`desktop-main-nav-item_${index}`}
                    type="button"
                    className={`navigation-tab focus:outline-none ${
                      isOpen && navigationState?.selectedItem
                        ? "bg-brand-primary-100 border-brand-primary-95 text-brand-primary-20"
                        : "border-transparent focus:shadow-outline"
                    }`}
                    onClick={() => onItemSelected(index)}
                  >
                    <span className="navigation-tab-text">{item.title}</span>
                    <svg
                      width={12}
                      height={12}
                      className={`${
                        isOpen ? "fill-current" : "stroke-current"
                      } transition-none flex-none`}
                    >
                      <use xlinkHref={isOpen ? closeIcon.url : arrowDownIcon.url} />
                    </svg>
                  </button>
                );
              })}
            </nav>
          </div>
        )}
        <GroupNavigation
          key="group-navigation-flyout"
          selectedTopLevel={navigationState?.selectedItemIndex}
          isMobile={isMobile}
          open={
            (isMobile && mobileNavigationOpen) ||
            (!isMobile && navigationState && navigationState.selectedItemIndex !== -1)
          }
          color={HeaderTheme.Primary}
          navigationItems={
            isMobile ? mainNavigation : navigationState?.selectedItem?.items ?? []
          }
          mobileToolLinks={siteSettings?.toolNavigation}
          onClickOutside={onClickOutside}
          onClickInside={onClickInside}
        />
      </div>
    </header>
  );
};

